import React, { useRef, useState } from 'react';
import { v4 } from 'uuid';
import { DomUtils } from '../../../util/dom.util';
import { Dropdown } from '../Dropdown';
import { Label } from '../Label';
import MultiTextInputItem from './MultiTextInputItem';
import MultiTextInputSuggestionItem, { DefaultMultiTextInputSuggestion } from './MultiTextInputSuggestion';

interface MultiTextInputProps {
  label: string;

  values: Set<string>;
  onValuesUpdated: (values: Set<string>) => void;
  validate?: (value: string) => boolean;
  /** Expects valid react key events (See the DOM Level 3 Events spec. for possible values) */
  keySeperators?: string[];

  itemBuilder?: (value: string) => { leading: React.ReactNode; text?: string };
  suggestionBuilder?: (value: string) => React.ReactNode;

  placeholder?: string;
}

export default function MultiTextInput({
  label,
  values,
  onValuesUpdated,
  validate = () => true,
  keySeperators = ['Enter', ',', ';', ' '], // Do not use 'Tab' here, this will limit accessibility!
  itemBuilder,
  suggestionBuilder,
  placeholder,
}: MultiTextInputProps) {
  const [id] = useState(v4());

  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState<string>('');
  const [focused, setFocused] = useState(false);

  const handleValidateAndAdd = (value: string = input) => {
    if (!value || !validate(value)) return;
    onValuesUpdated(new Set(values.add(input)));
    setInput('');
  };

  const handleRemove = (value: string) => {
    values.delete(value);
    onValuesUpdated(new Set(values));
    inputRef.current?.focus();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const pastedValues = pastedText.split(/[\s,;]+/);

    const validPastedValues = pastedValues
      .map(value => value.trim())
      .filter(value => value && validate(value) && !values.has(value));

    if (validPastedValues.length) onValuesUpdated(new Set([...values, ...validPastedValues]));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (keySeperators.includes(event.key)) {
      event.preventDefault();
      handleValidateAndAdd();
    } else if (event.key === 'Backspace') {
      if (!input && values.size) handleRemove([...values.values()][values.size - 1]);
    } else if (event.key === 'ArrowLeft') {
      if (!input && values.size) {
        event.preventDefault();
        const last = [...values.values()][values.size - 1];
        handleRemove(last);
        setInput(last);
      }
    }
  };

  const suggestionOffset = () => {
    if (!values.size) return -12;
    if (inputContainerRef.current && DomUtils.isElementFlexWrapped(inputContainerRef.current)) return -12;

    return 0;
  };

  return (
    <Label label={label} inputId={id}>
      <div className='flex flex-wrap gap-2 rounded-2 border border-gray-default p-3'>
        {[...values.values()].map(value => {
          const props = itemBuilder ? itemBuilder(value) : { text: value, leading: undefined };
          props.text ||= value;

          return (
            <MultiTextInputItem
              key={value}
              text={props.text}
              onRemove={event => {
                event.stopPropagation();
                handleRemove(value);
              }}
              leading={props.leading}
            />
          );
        })}

        <div className='relative flex flex-grow items-center' ref={inputContainerRef}>
          <Dropdown
            className='flex-grow'
            leftOffset={suggestionOffset()}
            width={'fit-content'}
            open={!!input && focused}
            setOpen={setFocused}
            dropdown={
              <MultiTextInputSuggestionItem
                valid={validate(input)}
                onClick={event => {
                  event.stopPropagation();
                  handleValidateAndAdd();
                }}
              >
                {suggestionBuilder ? suggestionBuilder(input) : <DefaultMultiTextInputSuggestion value={input} />}
              </MultiTextInputSuggestionItem>
            }
          >
            <input
              ref={inputRef}
              id={id}
              autoComplete='off'
              onFocus={() => setFocused(true)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onChange={event => setInput(event.currentTarget.value)}
              type={'text'}
              value={input}
              placeholder={placeholder}
              className='unset w-full flex-grow text-sm outline-none placeholder:text-gray-500'
            />
          </Dropdown>
        </div>
      </div>
    </Label>
  );
}
